<script setup lang="ts">
import {
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogOverlay,
    DialogPortal,
    DialogRoot,
    DialogTitle,
    DialogTrigger,
} from 'reka-ui'
import { CircleXIcon } from 'lucide-vue-next'
import { Motion, AnimatePresence } from 'motion-v'
import { useModal } from '@/composables/useModal'

const { isOpen } = defineProps({
    isOpen: {
        type: Boolean,
        default: false,
    },
    maxWidth: {
        type: String,
        default: 'max-w-md',
        validator: (value) => {
            return [
                'max-w-sm',
                'max-w-md',
                'max-w-lg',
                'max-w-xl',
                'max-w-2xl',
                'max-w-3xl',
                'max-w-4xl',
                'max-w-5xl',
                'max-w-6xl',
                'max-w-7xl',
                null,
            ].includes(String(value))
        },
    },
    maxHeight: {
        type: String,
        default: 'max-h-[85vh]',
    },
    variant: {
        type: String,
        default: 'default',
        validator: (value) => ['default', 'signal'].includes(String(value)),
    },
    triggerClass: {
        type: String,
        default: '',
    },
})

const emit = defineEmits(['update:isOpen'])

const { setDialogIsOpen } = useModal()

// Local open state seeded from prop
const open = ref(isOpen)

// Function to update both local state and emit event
function setOpen(value) {
    emit('update:isOpen', value)
    setDialogIsOpen(value)
    open.value = value
}

// Watch for open prop changes to update local state
watch(
    () => isOpen,
    (newVal) => {
        open.value = newVal
    }
)

provide('inDialog', true)
</script>

<template>
    <DialogRoot v-model:open="open" @update:open="(v) => setOpen(v)">
        <DialogTrigger type="button" class="flex" :class="triggerClass" @click.stop.prevent>
            <slot name="trigger" :open="open" :set-open="setOpen" />
        </DialogTrigger>
        <DialogPortal>
            <AnimatePresence>
                <DialogOverlay as-child>
                    <Motion
                        :initial="{ opacity: 0 }"
                        :animate="{ opacity: 1 }"
                        :exit="{ opacity: 0 }"
                        class="fixed inset-0 z-9998 bg-black/20"
                    />
                </DialogOverlay>
            </AnimatePresence>
            <AnimatePresence>
                <DialogContent
                    as-child
                    @interact-outside="(e) => e.preventDefault()"
                    @escape-key-down="(e) => e.preventDefault()"
                >
                    <Motion
                        :initial="{ opacity: 0, scale: 0.8, x: '-50%', y: '-50%' }"
                        :animate="{ opacity: 1, scale: 1, x: '-50%', y: '-50%' }"
                        :exit="{ opacity: 0, scale: 0.9, x: '-50%', y: '-50%' }"
                        class="fixed top-[50%] left-[50%] z-9999 flex w-[90vw] flex-col rounded-xl focus:outline-hidden"
                        :class="[
                            maxWidth,
                            maxHeight,
                            {
                                'border border-slate-300 bg-slate-50 p-0 shadow-2xl ring-6 ring-slate-50/50':
                                    variant === 'signal',
                                'bg-white p-[25px] shadow-lg': variant === 'default',
                            },
                        ]"
                    >
                        <div
                            v-if="$slots.title"
                            :class="[{ 'px-7 py-4': variant === 'signal' }]"
                            class="flex items-center justify-between"
                        >
                            <DialogTitle
                                :class="[
                                    {
                                        'font-brand text-base font-medium text-slate-500': variant === 'signal',
                                        'text-lg font-semibold text-slate-800': variant === 'default',
                                    },
                                ]"
                                class="m-0 w-full"
                            >
                                <slot name="title" />
                            </DialogTitle>
                            <DialogClose class="h-[25px] w-[25px]" aria-label="Close">
                                <Button variant="text" class="p-0" label="close">
                                    <template #icon>
                                        <CircleXIcon />
                                    </template>
                                </Button>
                            </DialogClose>
                        </div>
                        <div :class="[{ 'px-7': variant === 'signal' }]" class="flex flex-col overflow-auto">
                            <DialogDescription
                                :class="[{ 'rounded-lg bg-white px-7 py-6': variant === 'signal' }]"
                                class="h-full text-sm leading-normal"
                            >
                                <slot name="default" :close="() => setOpen(false)" />
                            </DialogDescription>
                        </div>

                        <DialogClose as-child>
                            <slot name="close-button" />
                        </DialogClose>
                        <div :class="[{ 'px-7 py-6': variant === 'signal' }]">
                            <slot name="footer" :close="() => setOpen(false)" />
                        </div>
                    </Motion>
                </DialogContent>
            </AnimatePresence>
        </DialogPortal>
    </DialogRoot>
</template>
